import {ALL_PRIVACY_CONSENT_KEYS, BASE_PRIVACY_CONSENT_KEYS, OPTIONAL_PRIVACY_CONSENT_KEYS, isOptionalConsentEditable} from 'src/constants/privacyConsents'

export function buildFormValues(user: Record<string, any>): Record<string, any> {
  return {
    title: user?.title ?? '',
    lastName: user?.lastName ?? '',
    firstName: user?.firstName ?? '',
    code: user?.code ?? '',
    fiscalCode: user?.fiscalCode ?? '',
    sex: (user?.sex && user.sex.length > 0) ? user.sex : '',
    nationality: user?.nationality ?? '',
    birthDate: user?.birthDate ?? null,
    createdAt: user?.createdAt ?? null,
    specialization: user?.specialization ?? '',
    prescribingDoctor: user?.prescribingDoctor ?? '',
    medicalCenters: user?.medicalCenters ?? [],
    operators: user?.operators ?? [],
    phoneNumber: user?.phoneNumber ?? '',
    cellPhoneNumber: user?.cellPhoneNumber ?? '',
    faxNumber: user?.faxNumber ?? '',
    email: user?.email ?? '',
    skypeId: user?.skypeId ?? '',
    country: user?.address?.country ?? 'IT',
    region: (user?.address?.region && user.address.region.length > 0) ? user.address.region : '',
    province: (user?.address?.province && user.address.province.length > 0) ? user.address.province : '',
    city: (user?.address?.city && user.address.city.length > 0) ? user.address.city : '',
    address: user?.address?.address ?? '',
    streetNumber: user?.address?.streetNumber ?? '',
    postalCode: user?.address?.postalCode ?? '',
    locality: user?.address?.locality ?? '',
    EMAIL: user?.preferredContacts?.includes('EMAIL') ? 'true' : 'false',
    SMS: user?.preferredContacts?.includes('SMS') ? 'true' : 'false',
    PUSH: user?.preferredContacts?.includes('PUSH') ? 'true' : 'false',
    caregiverId: user?.caregiverId ?? '',
    remindTherapyExpiryTypes: user?.remindTherapyExpiryTypes ?? [],
    therapyReminderTypes: user?.therapyReminderTypes ?? [],
    preferredContactMethods: user?.preferredContactMethods ?? [],
    enrollmentDate: user?.enrollmentDate ?? null,
    receiveModuleDate: user?.receiveModuleDate ?? null,
    ENROLLMENT: user?.privacyConsents?.includes('ENROLLMENT') ? 'true' : 'false',
    PRIVACY_PHONE_SURVEY: user?.privacyConsents?.includes('PRIVACY_PHONE_SURVEY') ? 'true' : 'false',
    PRIVACY_ENROLLMENT_APP_WEB: user?.privacyConsents?.includes('PRIVACY_ENROLLMENT_APP_WEB') ? 'true' : 'false',
    ...Object.fromEntries(OPTIONAL_PRIVACY_CONSENT_KEYS.map(key => [key, user?.privacyConsents?.includes(key) ? 'true' : 'false'])),
    mds: user?.mds?.toString() ?? '',
    testDBSService: user?.testDBSService?.toString() ?? '',
    serviceConf: user?.serviceConf?.toString() ?? '',
    drugDeliveryService: user?.drugDeliveryService ? ['true'] : [],
  }
}

function getParsedArray(values: Record<string, any>, optionsKeys: string[]): string[] | undefined {
  const valuesKeys = Object.keys(values)

  const contains = optionsKeys.some(element => valuesKeys.includes(element))
  if (!contains) {
    return undefined
  }

  const arrayValues: string[] = []
  optionsKeys.forEach((key: string) => {
    if (values[key]) {
      arrayValues.push(key)
    }
  })

  return arrayValues
}

function isReadOnlyField(fieldConfig: any): boolean {
  return fieldConfig?.readOnly === true
}

// Presence in profileConfiguration.fields can't be trusted for program/reminder-type fields:
// real PSPs have been found omitting them entirely (not even declaring readOnly) for a role
// that shouldn't edit them, which an opt-out check (exclude only if readOnly === true) can't
// catch. Requires the config to affirmatively say the field is there, visible, and editable.
function isFieldEditable(fieldConfig: any): boolean {
  return Boolean(fieldConfig) && fieldConfig.hidden !== true && fieldConfig.readOnly !== true
}

// Core identity/contact fields: opt-out (send unless the configurator explicitly marks them
// readOnly). Most PSPs never bother declaring code/fiscalCode at all, yet they're still meant
// to be sent, so these can't be flipped to opt-in without breaking them.
const CORE_FIELDS: Record<string, string> = {
  title: 'title', firstName: 'firstName', lastName: 'lastName', code: 'code', fiscalCode: 'fiscalCode',
  sex: 'sex', nationality: 'nationality', birthDate: 'birthDate',
  phoneNumber: 'phoneNumber', cellPhoneNumber: 'cellPhoneNumber', faxNumber: 'faxNumber',
  email: 'email', skypeId: 'skypeId',
}

// Program/reminder-type fields: opt-in (send only if the configurator affirmatively marks them
// editable). These are patient-program concepts that are often simply absent from a role's
// config rather than explicitly marked readOnly — see isFieldEditable.
const PROGRAM_FIELDS: Record<string, string> = {
  caregiverId: 'caregiverId',
  remindTherapyExpiryTypes: 'remindTherapyExpiryTypes', therapyReminderTypes: 'therapyReminderTypes',
  preferredContactMethods: 'preferredContactMethods', enrollmentDate: 'enrollmentDate',
  receiveModuleDate: 'receiveModuleDate', preferredContacts: 'preferredContacts',
  preferredTimeSlots: 'preferredTimeSlots',
}

const ADDRESS_SUBFIELDS = ['region', 'province', 'city', 'address', 'streetNumber', 'postalCode', 'locality', 'country']

export function createPatchBody(
  values: Record<string, any>,
  caregiver: Record<string, any> | undefined,
  timeslots: any[],
  fields: Record<string, any> = {}
): Record<string, any> {
  const patchValues: Record<string, any> = {
    ...values,
    birthDate: values.birthDate || undefined,
    address: {
      region: (Object.keys(values).includes('region') ? (values.region || '') : undefined),
      province: (Object.keys(values).includes('province') ? (values.province || '') : undefined),
      city: (Object.keys(values).includes('city') ? (values.city || '') : undefined),
      country: values.country,
      address: values.address,
      streetNumber: values.streetNumber,
      postalCode: values.postalCode,
      locality: values.locality
    },
    caregiverId: caregiver?._id,
    preferredContacts: getParsedArray(
      {EMAIL: values.EMAIL === 'true', SMS: values.SMS === 'true', PUSH: values.PUSH === 'true'},
      ['EMAIL', 'SMS', 'PUSH']
    ),
    receiveModuleDate: values.receiveModuleDate || undefined,
    enrollmentDate: values.enrollmentDate || undefined,
    // Every known consent is rebuilt from the form (seeded from the user in buildFormValues), so
    // consents the user can't see or edit here are carried over instead of being dropped.
    privacyConsents: getParsedArray(
      Object.fromEntries(ALL_PRIVACY_CONSENT_KEYS.map(key => [key, values[key] === 'true'])),
      ALL_PRIVACY_CONSENT_KEYS
    ),
    drugDeliveryService: values.drugDeliveryService?.length > 0 ? true : false,
    sex: (Object.keys(values).includes('sex') ? (values.sex || '') : undefined),
    preferredTimeSlots: timeslots
  }

  // Raw intermediate values folded above into preferredContacts/address/privacyConsents —
  // never valid top-level patch keys on their own.
  const rawIntermediateKeys = [
    'EMAIL', 'SMS', 'PUSH',
    'region', 'province', 'city', 'streetNumber', 'postalCode', 'locality', 'country',
    ...ALL_PRIVACY_CONSENT_KEYS,
  ]
  // Pure relationship/derived display data with no editable control anywhere in the profile
  // form (see UserDataSection.tsx) — never valid to write back through this endpoint. Also
  // specialization/mds/testDBSService/drugDeliveryService/serviceConf: readOnly: true in every
  // sampled PSP config, but not every PSP bothers to declare them at all, and an undeclared
  // field reads as "not read-only" — excluded unconditionally instead of trusting the
  // configurator to always mention them.
  const alwaysExcludedFields = [
    'prescribingDoctor', 'medicalCenter', 'medicalCenters', 'operators', 'createdAt',
    'specialization', 'mds', 'testDBSService', 'drugDeliveryService', 'serviceConf',
  ]

  rawIntermediateKeys.concat(alwaysExcludedFields).forEach(k => delete patchValues[k])

  Object.entries(CORE_FIELDS).forEach(([patchKey, configKey]) => {
    if (isReadOnlyField(fields?.[configKey])) {
      delete patchValues[patchKey]
    }
  })

  Object.entries(PROGRAM_FIELDS).forEach(([patchKey, configKey]) => {
    if (!isFieldEditable(fields?.[configKey])) {
      delete patchValues[patchKey]
    }
  })

  if (ADDRESS_SUBFIELDS.every(key => isReadOnlyField(fields?.address?.[key]))) {
    delete patchValues.address
  }

  if (
    BASE_PRIVACY_CONSENT_KEYS.every(key => isReadOnlyField(fields?.privacyConsents?.[key])) &&
    !OPTIONAL_PRIVACY_CONSENT_KEYS.some(key => isOptionalConsentEditable(fields?.privacyConsents, key))
  ) {
    delete patchValues.privacyConsents
  }

  Object.keys(patchValues).forEach(k => {
    if (patchValues[k] === undefined) delete patchValues[k]
  })

  return patchValues
}
